import { inject, Injectable } from '@angular/core';
import { ref, uploadBytesResumable, UploadTask, getDownloadURL, deleteObject } from '@angular/fire/storage';
import { FIREBASE_STORAGE} from '../../fireauth-lib.config';

@Injectable({
  providedIn: 'root'
})
export class ImageService {
  firebaseStorage = inject(FIREBASE_STORAGE);

  async uploadImage(imageName: string, image: File, userId?: string, orgCode?:string): Promise<UploadTask> {
    // Optionally include userId in the storage path as well.
    if (userId) {
      imageName = `${userId}/${imageName}`;
      const storageRef = ref(this.firebaseStorage, `images/${imageName}`);
      const metadata = {
        customMetadata: {
          userId: userId || '',
          orgCode: orgCode || ''
        }
      };
      return uploadBytesResumable(storageRef, image, metadata);
    } else {
      //Profile image
      const compressedImage = await this.resizeImage(image, 400, 80);
      const storageRef = ref(this.firebaseStorage, `images/${imageName}`);
      const metadata = {
        customMetadata: {
          userId: userId || ''
        }
      };
      return uploadBytesResumable(storageRef, compressedImage, metadata);
    }
  }

  async uploadProfileImage(imageName: string, image: File, userId?: string, orgCode?:string,size: number = 400, quality:number = 80 ): Promise<string> {
    // Compress the image
    const compressedImage = await this.resizeImage(image, size, quality);
    
    // Build path: profile/{userId}/{imageName} if userId provided, otherwise profile/{imageName}
    const path = userId ? `profile/${userId}/${imageName}` : `profile/${imageName}`;
    const storageRef = ref(this.firebaseStorage, path);
    
    const metadata = {
      customMetadata: {
        userId: userId || '',
        orgCode: orgCode || ''
      }
    };
    
    // Upload and wait for completion, then return download URL
    const uploadTask = uploadBytesResumable(storageRef, compressedImage, metadata);
    const snapshot = await uploadTask;
    return getDownloadURL(snapshot.ref);
  }

  /**
   * Compresses a support-ticket screenshot to JPEG and uploads it directly to
   * Firebase Storage — independent of the UserMedia/MediaAsset flows.
   * @returns The download URL and the storage path (needed to delete the file if the user removes it before submitting the ticket).
   */
  async uploadSupportTicketScreenshot(
    file: File,
    userId: string,
    orgId: string,
    size: number = 1600,
    quality: number = 82
  ): Promise<{ url: string; path: string }> {
    const compressedImage = await this.resizeImage(file, size, quality);
    const sanitizedName = file.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9-_]/g, '_');
    const path = `supporttickets/${userId}/${orgId}/${Date.now()}_${sanitizedName}.jpg`;
    const storageRef = ref(this.firebaseStorage, path);

    const metadata = {
      customMetadata: {
        userId,
        orgId
      }
    };

    const uploadTask = uploadBytesResumable(storageRef, compressedImage, metadata);
    const snapshot = await uploadTask;
    const url = await getDownloadURL(snapshot.ref);
    return { url, path };
  }

  /** Removes a previously-uploaded support-ticket screenshot the user has since removed. */
  async deleteSupportTicketScreenshot(path: string): Promise<void> {
    await deleteObject(ref(this.firebaseStorage, path));
  }
  
  /**
 * Resizes and compresses an image before uploading.
 * @param file The original image file.
 * @param maxSize The maximum width/height (whichever is larger).
 * @param quality Compression quality (0 to 100).
 * @returns A Blob of the resized image.
 */
private async resizeImage(file: File, maxSize: number, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
          const img = new Image();
          img.src = event.target?.result as string;
          img.onload = () => {
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");

              let width = img.width;
              let height = img.height;

              // Resize if image is larger than maxSize
              if (width > height) {
                  if (width > maxSize) {
                      height *= maxSize / width;
                      width = maxSize;
                  }
              } else {
                  if (height > maxSize) {
                      width *= maxSize / height;
                      height = maxSize;
                  }
              }

              // Set canvas size
              canvas.width = width;
              canvas.height = height;

              // Draw the resized image onto the canvas
              ctx?.drawImage(img, 0, 0, width, height);

              // Convert canvas to Blob (JPEG)
              canvas.toBlob(
                  (blob) => {
                      if (blob) resolve(blob);
                      else reject(new Error("Image compression failed"));
                  },
                  "image/jpeg",
                  quality / 100 // Convert percentage to decimal (e.g., 80% → 0.8)
              );
          };
      };
      reader.onerror = (error) => reject(error);
  });
}
}
